'use client';

import { useId, useState, type ReactNode } from 'react';
import { cn } from '../lib/cn';

type CollapsibleSectionProps = {
  title: string;
  description?: string;
  summary?: ReactNode;
  density?: 'default' | 'compact';
  defaultOpen?: boolean;
  tone?: 'default' | 'error';
  className?: string;
  children: ReactNode;
};

/**
 * 헤더(제목+설명+chevron)를 클릭해 본문을 접고 펴는 카드 섹션. 기본은 접힘.
 * 워크스페이스 모달의 결제/시트·위험 구역 접기 패턴과 동일한 시각 규칙을 따른다.
 */
export function CollapsibleSection({
  title,
  description,
  summary,
  density = 'default',
  defaultOpen = false,
  tone = 'default',
  className,
  children,
}: CollapsibleSectionProps) {
  const [open, setOpen] = useState(defaultOpen);
  // 접힌 상태에서도 섹션을 이름으로 찾을 수 있어야 하므로 제목을 region 이름으로 연결한다.
  const titleId = useId();

  return (
    <section
      aria-labelledby={titleId}
      className={cn(
        density === 'compact' ? 'min-w-0' : 'rounded-2xl border bg-base-100 shadow-xs',
        tone === 'error' ? 'border-error/20 bg-error/5' : 'border-base-300',
        className,
      )}
    >
      <button
        type="button"
        className={cn(
          'flex w-full justify-between gap-3 text-left focus-visible:outline-2 focus-visible:outline-offset-2 focus-visible:outline-primary',
          density === 'compact' ? 'min-h-11 items-center py-2' : 'items-start p-4 sm:p-5',
        )}
        aria-expanded={open}
        onClick={() => setOpen((value) => !value)}
      >
        <div className={cn('min-w-0', density === 'compact' && 'flex flex-wrap items-baseline gap-x-3 text-sm')}>
          <h3
            id={titleId}
            className={cn('text-sm font-semibold', tone === 'error' ? 'text-error' : 'text-base-content')}
          >
            {title}
          </h3>
          {summary}
          {description ? <p className="mt-1 text-xs leading-5 text-[var(--color-fg-muted)]">{description}</p> : null}
        </div>
        <svg
          xmlns="http://www.w3.org/2000/svg"
          className={cn(
            'mt-0.5 h-4 w-4 shrink-0 text-[var(--color-fg-muted)] transition-transform',
            open && 'rotate-180',
          )}
          fill="none"
          viewBox="0 0 24 24"
          stroke="currentColor"
          aria-hidden
        >
          <path strokeLinecap="round" strokeLinejoin="round" strokeWidth="2" d="M19 9l-7 7-7-7" />
        </svg>
      </button>
      {open ? <div className={density === 'compact' ? 'pb-2' : 'px-4 pb-4 sm:px-5 sm:pb-5'}>{children}</div> : null}
    </section>
  );
}
